import { SourceContinuityTransport, type SourceContinuity, type SourceLane } from './sourceContinuity';

export interface SourceObservation {
  lane?: unknown; sourceEpoch?: unknown; sourceGen?: unknown;
  observedAtMs?: unknown; snapshotMs?: unknown; pendingResync?: unknown;
  source?: unknown; sport?: unknown;
}
export interface SourceProof {
  feed: string;
  session: object;
  kind: 'market' | 'orderbook';
  announced: boolean;
  row: SourceObservation;
}
export type SourceVerdict = 'continuous' | 'unconfirmed';
const positive = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

/** Authority belongs to a configured feed AND its current transport session.
 * Row timestamps are producer observations, never subtracted from browser time.
 * Quiet rows remain valid under their exact governing lane generation.
 */
export class FeedSourceAuthority {
  private feeds = new Map<string, { session: object; controls: SourceContinuityTransport }>();
  require(feed: string): void {
    this.feeds.set(feed, { session: {}, controls: new SourceContinuityTransport() });
  }
  required(feed: string | undefined): boolean { return feed != null && this.feeds.has(feed); }
  retain(feeds: ReadonlySet<string>): void {
    for (const feed of this.feeds.keys()) if (!feeds.has(feed)) this.feeds.delete(feed);
  }
  control(feed: string, frame: SourceContinuity | null, invalidLane?: SourceLane): void {
    const state = this.feeds.get(feed);
    if (!state) return;
    if (frame === null && invalidLane) state.controls.quarantine(invalidLane);
    else if (frame === null) { state.session = {}; state.controls.reset(); }
    else state.controls.receive(frame);
  }
  capture(feed: string | undefined, kind: SourceProof['kind'], row?: SourceObservation): SourceProof | undefined {
    const state = feed == null ? undefined : this.feeds.get(feed);
    const governing = state?.controls.get(row?.lane as SourceLane);
    return state && feed != null ? { feed, session: state.session, kind,
      announced: governing != null && governing.sourceEpoch === row?.sourceEpoch && governing.sourceGen === row?.sourceGen,
      row: {
      lane: row?.lane, sourceEpoch: row?.sourceEpoch, sourceGen: row?.sourceGen,
      observedAtMs: row?.observedAtMs, snapshotMs: row?.snapshotMs, pendingResync: row?.pendingResync,
      source: row?.source, sport: row?.sport,
    } } : undefined;
  }
  verdict(proof: SourceProof | undefined): SourceVerdict | undefined {
    if (!proof) return undefined; // explicitly legacy or synthetic caller
    const state = this.feeds.get(proof.feed), row = proof.row;
    if (!state || state.session !== proof.session || !proof.announced || !positive(row.observedAtMs)
      || typeof row.sourceEpoch !== 'string' || !Number.isSafeInteger(row.sourceGen)
      || (row.sourceGen as number) < 0 || typeof row.lane !== 'string') return 'unconfirmed';
    const bookLane = row.lane === 'kalshi-orderbook' || row.lane.startsWith('kalshi-orderbook:') || row.lane.startsWith('poly-book:');
    const marketLane = row.lane === 'kalshi-ticker' || row.lane === 'poly-floor'
      || row.lane.startsWith('kalshi-ticker:') || row.lane.startsWith('poly-book:');
    if (proof.kind === 'market' ? !marketLane : (!bookLane || !positive(row.snapshotMs)
      || row.snapshotMs > row.observedAtMs
      || (row.pendingResync !== undefined && row.pendingResync !== false))) return 'unconfirmed';
    if (row.source === 'kalshi' && !row.lane.startsWith('kalshi-')
      || row.source === 'polymarket' && !row.lane.startsWith('poly-')) return 'unconfirmed';
    const suffix = row.lane.split(':')[1];
    if (suffix && typeof row.sport === 'string' && suffix !== row.sport.toUpperCase()) return 'unconfirmed';
    const governing = state.controls.get(row.lane as SourceLane);
    return governing?.state === 'valid' && governing.sourceEpoch === row.sourceEpoch
      && governing.sourceGen === row.sourceGen ? 'continuous' : 'unconfirmed';
  }
  /** Queued old-generation data may follow its successor's announcement.
   * Keep it for initial display only; never overwrite a row already observed
   * under the current authority with a retired generation's value. */
  supersedes(prior: SourceProof | undefined, next: SourceProof | undefined): boolean {
    if (prior && next && prior.feed === next.feed && prior.session === next.session) {
      const control = this.feeds.get(next.feed)?.controls.get(next.row.lane as SourceLane);
      if (control && typeof next.row.sourceEpoch === 'string' && Number.isSafeInteger(next.row.sourceGen)
        && (next.row.sourceEpoch !== control.sourceEpoch || (next.row.sourceGen as number) < control.sourceGen)
        && this.verdict(prior) === 'continuous') return false;
    }
    if (!prior || !next || prior.feed !== next.feed || prior.session !== next.session
      || prior.row.lane !== next.row.lane) return true;
    if (prior.row.sourceEpoch !== next.row.sourceEpoch) return this.verdict(prior) !== 'continuous';
    return !(typeof prior.row.sourceGen === 'number' && typeof next.row.sourceGen === 'number'
      && next.row.sourceGen < prior.row.sourceGen);
  }
}

/** Negotiation is per connection; preserve every other subscription field. */
export function withSourceEvidence<T extends Record<string, unknown>>(msg: T, enabled: boolean): T {
  return enabled && msg.op === 'subscribe' && ['markets','orderbooks','all'].includes(msg.channel as string)
    ? { ...msg, evidence: 1 } : msg;
}
